import React from 'react';
import { Card, CardContent } from '@shadcn/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shadcn/components/ui/table';
import { Button } from '@shadcn/components/ui/button';
import { Input } from "@shadcn/components/ui/input";
import { Label } from "@shadcn/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@shadcn/components/ui/tooltip";
import { ChevronUp, ChevronDown } from 'lucide-react';
import ExportButton from './ExportButton';

// Helper functions
const isCurrentMonth = (monthStr) => {
  const monthDate = new Date(monthStr);
  const currentDate = new Date();
  return monthDate.getMonth() === currentDate.getMonth() && 
         monthDate.getFullYear() === currentDate.getFullYear();
};

const formatContainerDetails = (containers) => {
  if (!containers || containers.length === 0) return <span>No containers</span>;
  
  return containers.map((container, index) => (
    <div key={index} className="text-xs border-b border-gray-100 pb-1 mb-1 last:border-b-0 last:pb-0 last:mb-0">
      <div className="font-medium">{container.containerReference}</div>
      <div className="text-gray-600">Qty: {container.quantity}</div>
      <div className="text-gray-600">ETA: {container.etaDate}</div>
    </div>
  ));
};

const SortableHeader = ({ label, sortKey, sortConfig, requestSort, className, style }) => {
  const getSortIcon = () => {
    if (sortConfig.key !== sortKey) {
      return <ChevronDown className="h-4 w-4 text-gray-400" />;
    }
    return sortConfig.direction === 'ascending' 
      ? <ChevronUp className="h-4 w-4 text-blue-600" />
      : <ChevronDown className="h-4 w-4 text-blue-600" />;
  };

  return (
    <TableHead className={className} style={style}>
      <Button
        variant="ghost"
        onClick={() => requestSort(sortKey)}
        className="h-auto p-0 font-semibold text-left justify-start hover:bg-transparent w-full"
      >
        <span className="mr-1">{label}</span>
        {getSortIcon()}
      </Button>
    </TableHead>
  );
};

const DetailedTable = ({
  loading,
  error,
  filteredAndSortedTableData,
  tableData,
  forecastData,
  selectedRegion,
  viewType,
  selectedMonths,
  searchTerm,
  setSearchTerm,
  sortConfig,
  requestSort,
}) => {
  return (
    <Card className="mb-6">
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-xl font-semibold">Monthly Forecast Details</h4>
          
          <div className="flex items-center gap-4">
            <ExportButton
              data={filteredAndSortedTableData}
              forecastData={forecastData}
              selectedRegion={selectedRegion}
              selectedMonths={selectedMonths}
              viewType={viewType}
              filters={{
                searchTerm
              }}
            />
          </div>
        </div>

        {/* Standalone Search Input */}
        <div className="mb-4 max-w-md">
          <Label className="text-sm font-medium text-gray-700 mb-2 block">
            Search
          </Label>
          <Input
            placeholder={`Search by ${viewType === 'sku' ? 'SKU' : viewType === 'category' ? 'category' : 'product name'}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
          {searchTerm && (
            <div className="mt-2 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Showing {filteredAndSortedTableData.length} of {tableData.length} records
              </p>
              <Button
                onClick={() => setSearchTerm('')}
                variant="outline"
                size="sm"
                className="text-xs"
              >
                Clear
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-40">
            <div className="flex flex-col items-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
              <span className="mt-2 text-sm text-gray-500">Loading data...</span>
            </div>
          </div>
        ) : error ? (
          <div className="text-center text-red-500 py-6">
            {error}
          </div>
        ) : filteredAndSortedTableData.length > 0 ? (
          <div className="overflow-x-auto overflow-y-visible pb-4 shadow border rounded-lg">
            <div className="text-center mb-2 text-xs text-gray-500 lg:hidden">
              ← Scroll horizontally to view all columns →
            </div>
            
            <Table className="min-w-max w-full text-sm">
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <SortableHeader
                    label={viewType === 'sku' ? 'SKU' : viewType === 'category' ? 'Category' : 'Product'}
                    sortKey={viewType === 'sku' ? 'sku' : 'name'}
                    sortConfig={sortConfig}
                    requestSort={requestSort}
                    className="sticky left-0 z-20 bg-gray-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"
                    style={{ minWidth: '200px' }}
                  />
                  
                  <SortableHeader
                    label="Current Stock"
                    sortKey="currentStock"
                    sortConfig={sortConfig}
                    requestSort={requestSort}
                    className="text-right bg-gray-50 sticky left-[200px] z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"
                    style={{ minWidth: '120px' }}
                  />
                  
                  <SortableHeader
                    label="Month Cover"
                    sortKey="calculatedMonthCover"
                    sortConfig={sortConfig}
                    requestSort={requestSort}
                    className="text-right bg-gray-50 sticky left-[320px] z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"
                    style={{ minWidth: '120px' }}
                  />
                  
                  <SortableHeader
                    label="Status"
                    sortKey="inventoryStatus"
                    sortConfig={sortConfig}
                    requestSort={requestSort}
                    className="text-center bg-gray-50 sticky left-[440px] z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"
                    style={{ minWidth: '120px' }}
                  />
                  
                  {viewType === 'sku' && (
                    <SortableHeader
                      label="Stock Out Date"
                      sortKey="sortableStockOutDate"
                      sortConfig={sortConfig}
                      requestSort={requestSort}
                      className="text-right bg-gray-50 sticky left-[560px] z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"
                      style={{ minWidth: '120px' }}
                    />
                  )}
                  
                  {selectedMonths.map(month => (
                    <React.Fragment key={`${month}-header`}>
                      <TableHead 
                        colSpan={isCurrentMonth(month) ? 5 : 4} 
                        className="text-center border-l border-gray-200"
                      >
                        {month}
                      </TableHead>
                    </React.Fragment>
                  ))}
                </TableRow>
                
                <TableRow>
                  <TableHead className="sticky left-0 z-20 bg-white shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"></TableHead>
                  <TableHead className="text-right sticky left-[200px] z-20 bg-white shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"></TableHead>
                  <TableHead className="text-right sticky left-[320px] z-20 bg-white shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"></TableHead>
                  <TableHead className="text-center sticky left-[440px] z-20 bg-white shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"></TableHead>
                  
                  {viewType === 'sku' && (
                    <TableHead className="text-right sticky left-[560px] z-20 bg-white shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"></TableHead>
                  )}
                  
                  {selectedMonths.map(month => (
                    <React.Fragment key={`${month}-details`}>
                      <TableHead className="text-right border-l border-gray-200">Forecast</TableHead>
                      <TableHead className="text-right">Actual</TableHead>
                      <TableHead className="text-right">Accuracy</TableHead>
                      {isCurrentMonth(month) && (
                        <TableHead className="text-right">MTD Sales</TableHead>
                      )}
                      <TableHead className="text-right">Upcoming</TableHead>
                    </React.Fragment>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedTableData.map((item, index) => (
                  <TableRow key={item.key} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <TableCell 
                      className="font-medium sticky left-0 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" 
                      style={{ 
                        backgroundColor: index % 2 === 0 ? '#f9fafb' : 'white',
                        minWidth: '200px'
                      }}
                    >
                      {viewType === 'sku' 
                        ? item.sku 
                        : viewType === 'category' 
                          ? item.name 
                          : <div className="max-w-[200px] truncate">{item.productName || item.name}</div>
                      }
                    </TableCell>
                    
                    <TableCell 
                      className="text-right sticky left-[200px] z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" 
                      style={{ 
                        backgroundColor: index % 2 === 0 ? '#f9fafb' : 'white',
                        minWidth: '120px'
                      }}
                    >
                      {item.currentStock}
                    </TableCell>
                    
                    <TableCell 
                      className="text-right sticky left-[320px] z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" 
                      style={{ 
                        backgroundColor: index % 2 === 0 ? '#f9fafb' : 'white',
                        minWidth: '120px'
                      }}
                    >
                      {item.calculatedMonthCover !== 'NaN' && item.calculatedMonthCover !== '0.00' ? 
                        <span className={
                          parseFloat(item.calculatedMonthCover) < 1 ? 'text-red-500 font-medium' : 
                          parseFloat(item.calculatedMonthCover) < 2 ? 'text-orange-500 font-medium' : 
                          'text-green-500 font-medium'
                        }>
                          {item.calculatedMonthCover}
                        </span> : 
                        'N/A'
                      }
                    </TableCell>
                    
                    <TableCell 
                      className="text-center sticky left-[440px] z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" 
                      style={{ 
                        backgroundColor: index % 2 === 0 ? '#f9fafb' : 'white',
                        minWidth: '120px'
                      }}
                    >
                      {item.inventoryStatus && item.calculatedMonthCover !== 'NaN' ? (
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${item.inventoryStatus.color}`}>
                          {item.inventoryStatus.label}
                        </span>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    
                    {viewType === 'sku' && (
                      <TableCell 
                        className="text-right sticky left-[560px] z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" 
                        style={{ 
                          backgroundColor: index % 2 === 0 ? '#f9fafb' : 'white',
                          minWidth: '120px'
                        }}
                      >
                        <span className={
                          item.calculatedStockOutDate !== 'N/A' && item.calculatedStockOutDate !== '-' ? 'text-red-500 font-medium' : 'text-gray-500'
                        }>
                          {item.calculatedStockOutDate || '-'}
                        </span>
                      </TableCell>
                    )}
                    
                    {selectedMonths.map(month => (
                      <React.Fragment key={`${month}-data-${item.key}`}>
                        <TableCell className="text-right border-l border-gray-200">
                          {item[`${month}_forecast`] || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          {item[`${month}_actual`] !== null ? item[`${month}_actual`] : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          {item[`${month}_accuracy`] !== null ? 
                            <span className={
                              parseFloat(item[`${month}_accuracy`]) > 90 ? 'text-green-500 font-medium' : 
                              parseFloat(item[`${month}_accuracy`]) > 70 ? 'text-yellow-500 font-medium' : 
                              'text-red-500 font-medium'
                            }>
                              {item[`${month}_accuracy`]}%
                            </span> : 
                            '-'
                          }
                        </TableCell>
                        {isCurrentMonth(month) && (
                          <TableCell className="text-right">
                            {item[`${month}_mtdSales`] !== null ? 
                              <div>
                                <span>{item[`${month}_mtdSales`]}</span>
                                {item[`${month}_mtdDaysPassed`] !== null && 
                                  <span className="text-xs text-gray-500 ml-1">
                                    ({item[`${month}_mtdDaysPassed`]} days)
                                  </span>
                                }
                              </div>
                              : '-'
                            }
                          </TableCell>
                        )}
                        <TableCell className="text-right">
                          {item[`${month}_upcomingDetails`]?.length > 0 ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center justify-end cursor-pointer">
                                    <span>{item[`${month}_upcoming`]}</span>
                                    <span className="ml-1 text-xs text-blue-500 hover:underline">(details)</span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent 
                                  side="right" 
                                  align="start"
                                  className="w-[280px] sm:w-[300px] p-0 z-50 border-gray-200 max-h-[350px] overflow-hidden"
                                  sideOffset={5}
                                >
                                  <div className="p-3">
                                    <h5 className="text-sm font-medium mb-2">Container Details</h5>
                                    <div className="max-h-[200px] sm:max-h-[250px] overflow-y-auto custom-scrollbar">
                                      {formatContainerDetails(item[`${month}_upcomingDetails`])}
                                    </div>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            item[`${month}_upcoming`] || '-'
                          )}
                        </TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : tableData.length > 0 ? (
          <div className="text-center py-6">
            <p>No records match your current search.</p>
            <Button
              onClick={() => setSearchTerm('')}
              variant="outline"
              className="mt-2"
            >
              Clear search
            </Button>
          </div>
        ) : (
          <div className="text-center py-6">
            No data available
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DetailedTable;
